import type { ContactFormValues, WireBodyFields } from './types';

/**
 * The Contact body's wire boundary — the sibling of `./body-html.ts`, and the
 * second of the two places this feature resolves a shape disagreement.
 *
 * **All three disagreements this module used to carry are settled**, and the
 * settlement is why the page can be saved at all: every save answered `400
 * CONTENT_BAD_INPUT` while they stood, whether or not the operator had changed a
 * field.
 *
 * - **`lede` is gone.** It had no column on either side and no reader anywhere:
 *   `frontend-landing`'s About fetcher hardcodes `lede: null` with a note saying
 *   no field carries one, and the Contact page's lede is a constant in that
 *   repo's own copy. It was sent on every save, is not a member of
 *   `CONTACT_FIELDS`, and was the first thing the service rejected.
 * - **`map` keeps the catalog's shape**, and the service now accepts it. The
 *   `map_render` row declares the location «stored as a URL and drawn as a link
 *   that opens in a new tab — no map tiles, no map SDK, and no iframe», and a
 *   coordinate pair needs exactly the map that row forbids.
 * - **`social` takes the service's spelling.** It declares
 *   `{network, handle, url}` and the public page READS that: `readSocial` in
 *   `frontend-landing` drops any row carrying neither `handle` nor `network`. So
 *   `{label, url}` was refused on write and would have rendered nothing had it
 *   been stored — two sides agreed and this console was the outlier. `handle`
 *   carries the free label the pack draws; no `network` vocabulary is invented
 *   and none is sent.
 */

export function toFormValues(fields: WireBodyFields | null | undefined): ContactFormValues {
  return {
    supportPhone: fields?.support_phone ?? '',
    email: fields?.email ?? '',
    address: fields?.address ?? '',
    mapLabel: fields?.map?.label ?? '',
    mapUrl: fields?.map?.url ?? '',
    social: (fields?.social ?? []).map((entry) => ({
      // `network` is read as a fallback so a row seeded with one still shows a
      // name in the control rather than an empty field the operator must retype.
      handle: entry.handle ?? entry.network ?? '',
      url: entry.url ?? '',
    })),
  };
}

/**
 * The form, back onto the wire.
 *
 * A row the operator left completely blank is **dropped** rather than sent as a
 * pair of empty strings: the public page removes an empty channel from its list,
 * and storing an empty row would make the two disagree about how many handles
 * the operator configured.
 */
export function toWireFields(values: ContactFormValues): WireBodyFields {
  return {
    support_phone: values.supportPhone,
    email: values.email,
    address: values.address,
    map: { label: values.mapLabel, url: values.mapUrl },
    social: values.social.filter((entry) => entry.handle.length > 0 || entry.url.length > 0),
  };
}
